import { useState } from 'react';
import { SKILLS, ABILITY_NAMES, ABILITY_PASTELS, ABILITY_PASTELS_LIGHT } from '@shared/dnd-data';
import { PartyCharacterSkill, SkillName, ProficiencyLevel } from '@shared/types';
import { useTheme } from '../lib/useTheme';

interface Props {
  characterSkills: PartyCharacterSkill[];
}

// WHY: Leading + keeps bonus values column-aligned and instantly readable
// during a session (e.g. "+7" vs "-1") so the table can scan quickly.
function formatBonus(bonus: number): string {
  return bonus >= 0 ? `+${bonus}` : `${bonus}`;
}

// WHY: Passive scores in D&D 5e are 10 + the skill modifier. This helper
// centralises the rule so the formula isn't duplicated and can be unit-tested
// independently of the UI.
// RULE: PHB p.175 — Passive score = 10 + ability modifier
function getPassiveScore(skillBonus: number): number {
  return 10 + skillBonus;
}

type Tab = 'active' | 'passive';

// WHY: Perception, Insight, and Investigation are the three skills that
// D&D 5e uses for passive checks (hidden creatures, lying, hidden objects).
// Other skills don't have passive equivalents in the rules.
// RULE: PHB p.175 — Passive Perception is the only passive skill explicitly
// called out, but Insight and Investigation follow the same pattern.
const PASSIVE_SKILLS: SkillName[] = ['perception', 'insight', 'investigation'];

export default function SkillReferencePanel({ characterSkills }: Props) {
  const { theme } = useTheme();
  const pastels = theme === 'light' ? ABILITY_PASTELS_LIGHT : ABILITY_PASTELS;
  const [tab, setTab] = useState<Tab>('active');

  const renderSkills = () => {
    if (tab === 'active') {
      // WHY: Stable, alphabetical skill order so the panel is predictable to scan
      // every session rather than reordering by whatever data comes back first.
      const sortedSkills = (Object.values(SKILLS) as { name: SkillName; displayName: string; ability: keyof typeof ABILITY_NAMES }[])
        .slice()
        .sort((a, b) => a.displayName.localeCompare(b.displayName));

      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {sortedSkills.map((skill) => {
            const accent = pastels[skill.ability];

            const ranked = characterSkills
              .map((char) => ({ name: char.name, bonus: char.skills[skill.name] ?? 0, proficiency: char.proficiencies[skill.name] ?? 'none' }))
              // WHY: Best→worst by bonus, ties broken by name so the order is
              // deterministic and the "go-to" pick is unambiguous.
              .sort((a, b) => b.bonus - a.bonus || a.name.localeCompare(b.name));

            return (
              <div
                key={skill.name}
                className="panel border-l-4 p-3"
                style={{ borderColor: accent }}
              >
                <div className="flex items-baseline justify-between mb-2">
                  <span className="text-sm font-medium">{skill.displayName}</span>
                  <span
                    className="text-xs uppercase tracking-wide"
                    style={{ color: accent }}
                  >
                    {ABILITY_NAMES[skill.ability]}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {ranked.map((member) => {
                    const isProficient = member.proficiency === 'proficient' || member.proficiency === 'expertise';
                    return (
                      <span
                        key={member.name}
                        className={`px-3 py-1 rounded text-sm ${
                          isProficient
                            ? 'panel border-dnd-success text-dnd-success font-semibold'
                            : 'text-dnd-muted'
                        }`}
                      >
                        {member.name} {formatBonus(member.bonus)}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Passive tab: Perception, Insight, Investigation in that order
    // WHY: A 3-column grid on desktop gives each skill card comfortable width
    // since there are exactly three passive skills to display.
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {PASSIVE_SKILLS.map((skillName) => {
          const skill = SKILLS[skillName];
          const accent = pastels[skill.ability];

          const ranked = characterSkills
            .map((char) => ({
              name: char.name,
              passiveScore: getPassiveScore(char.skills[skillName] ?? 0),
              proficiency: char.proficiencies[skillName] ?? 'none',
            }))
            // WHY: Best→worst by passive score, ties broken by name so the order
            // is deterministic and the "go-to" pick is unambiguous.
            .sort((a, b) => b.passiveScore - a.passiveScore || a.name.localeCompare(b.name));

          return (
            <div
              key={skillName}
              className="panel border-l-4 p-3"
              style={{ borderColor: accent }}
            >
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-sm font-medium">{skill.displayName}</span>
                <span
                  className="text-xs uppercase tracking-wide"
                  style={{ color: accent }}
                >
                  {ABILITY_NAMES[skill.ability]}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {ranked.map((member) => {
                  const isProficient = member.proficiency === 'proficient' || member.proficiency === 'expertise';
                  return (
                    <span
                      key={member.name}
                  className={`px-3 py-1 rounded text-sm ${
                    isProficient
                      ? 'panel border-dnd-success text-dnd-success font-semibold'
                      : 'text-dnd-muted'
                  }`}
                >
                  {member.name} {member.passiveScore}
                    </span>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (characterSkills.length === 0) {
    return (
      <div className="card mb-6">
        <h3 className="text-lg font-semibold mb-3">Skill Check Reference</h3>
        <p className="text-dnd-muted">No party members</p>
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-lg font-semibold">Skill Check Reference</h3>
        <div className="flex flex-wrap gap-2 radar-tab-buttons">
          <button
            type="button"
            onClick={() => setTab('active')}
            aria-pressed={tab === 'active'}
            className={`btn ${tab === 'active' ? 'btn-gold' : 'tab-btn'}`}
          >
            Active
          </button>
          <button
            type="button"
            onClick={() => setTab('passive')}
            aria-pressed={tab === 'passive'}
            className={`btn ${tab === 'passive' ? 'btn-gold' : 'tab-btn'}`}
          >
            Passive
          </button>
        </div>
      </div>
      {renderSkills()}
    </div>
  );
}

